import { create } from "zustand";
import type { SessionUser } from "@homecad/schema";
import { appelerApi, NonAuthentifie } from "../api/client.js";

/**
 * L'etat d'authentification du front. Trois etats et pas deux : « inconnu » est
 * celui du premier instant, avant que `/api/auth/me` ait repondu. Le confondre
 * avec « anonyme » ferait clignoter l'ecran de connexion a chaque ouverture,
 * y compris pour quelqu'un de deja connecte.
 */
export type EtatSession = "inconnu" | "anonyme" | "connecte";

interface MagasinSession {
  etat: EtatSession;
  utilisateur: SessionUser | undefined;
  erreur: string | undefined;
  rafraichir: () => Promise<void>;
  connecter: (login: string, motDePasse: string) => Promise<void>;
  deconnecter: () => Promise<void>;
  /** Appele quand un appel quelconque rend 401 : le front s'aligne sur le serveur. */
  expirer: () => void;
}

export const useSession = create<MagasinSession>((set) => ({
  etat: "inconnu",
  utilisateur: undefined,
  erreur: undefined,

  rafraichir: async () => {
    try {
      const utilisateur = await appelerApi<SessionUser>("/auth/me");
      set({ etat: "connecte", utilisateur, erreur: undefined });
    } catch (cause) {
      if (cause instanceof NonAuthentifie) {
        set({ etat: "anonyme", utilisateur: undefined });
        return;
      }
      // L'API est injoignable : on ne sait pas si la session vaut encore. Rester
      // sur « inconnu » afficherait une attente infinie, on montre donc le
      // formulaire avec la vraie cause.
      set({
        etat: "anonyme",
        utilisateur: undefined,
        erreur: cause instanceof Error ? cause.message : String(cause),
      });
    }
  },

  connecter: async (login, motDePasse) => {
    set({ erreur: undefined });
    try {
      const utilisateur = await appelerApi<SessionUser>("/auth/login", {
        methode: "POST",
        corps: { login, motDePasse },
      });
      set({ etat: "connecte", utilisateur, erreur: undefined });
    } catch (cause) {
      // Le serveur ne rend qu'un seul message d'echec, volontairement : on le
      // reprend tel quel plutot que d'en inventer un plus precis.
      set({
        etat: "anonyme",
        utilisateur: undefined,
        erreur:
          cause instanceof NonAuthentifie
            ? "Identifiants invalides"
            : cause instanceof Error
              ? cause.message
              : String(cause),
      });
    }
  },

  deconnecter: async () => {
    try {
      await appelerApi<void>("/auth/logout", { methode: "POST" });
    } catch {
      // Meme si l'appel echoue, le front doit repasser anonyme : le cookie est
      // peut-etre deja mort cote serveur.
    }
    set({ etat: "anonyme", utilisateur: undefined, erreur: undefined });
  },

  expirer: () => set({ etat: "anonyme", utilisateur: undefined, erreur: undefined }),
}));
